import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import greenBtn from "../img/green_button.png";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "./hallTimer";
import {
  CROSSWORD_WORDS,
  CROSSWORD_POINTS,
  CROSSWORD_HINT_PENALTY,
  CROSSWORD_UNLOCKS,
  buildCrosswordModel,
  cellKey,
  parseCellKey,
  createEmptyFill,
  isCrosswordSolved,
  scoreCrosswordFill,
  neighborCellKey,
  findWordAtCell,
  getWordCellKeys,
  siblingKeyInWord,
  inferCellDirection,
  isWordComplete,
  revealLetterInWord,
} from "./crosswordData";
import { unlockHall1Hotspot, markHall1HotspotCompleted, getRedirectIfHall1StageDone } from "./medalDisplayData";
import deskBg from "./stage4/stage4_desk.jpg";
import mobBg from "./stage4/stage4_mob.jpg";
import cluesBox from "./stage4/box.png";
import "../fonts/breuer-headline.css";
import "./stage4.css";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function focusCellInput(row, col) {
  const input = document.querySelector(
    `[data-cw-row="${row}"][data-cw-col="${col}"]`
  );
  if (input && !input.disabled) {
    input.focus();
    input.select?.();
    return true;
  }
  return false;
}

/** Hall Stage 4 — Sports Archive Crossword */
export default function HallStage4() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const model = useMemo(() => buildCrosswordModel(CROSSWORD_WORDS), []);
  const { rows, cols, solution, cells, numbers } = model;
  const acrossWords = useMemo(
    () => CROSSWORD_WORDS.filter((w) => w.dir === "across"),
    []
  );
  const downWords = useMemo(
    () => CROSSWORD_WORDS.filter((w) => w.dir === "down"),
    []
  );

  const [fill, setFill] = useState(() => createEmptyFill(solution));
  const [cellStates, setCellStates] = useState({});
  const [activeWordId, setActiveWordId] = useState(
    () => CROSSWORD_WORDS[0]?.id || null
  );
  const [dir, setDir] = useState(
    () => CROSSWORD_WORDS[0]?.dir || "across"
  );
  const [completedWords, setCompletedWords] = useState(() => new Set());
  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [shake, setShake] = useState(false);
  const [flashOk, setFlashOk] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(CROSSWORD_POINTS);
  const [hintsUsed, setHintsUsed] = useState(0);
  /** Cell keys filled by HINT — excluded from live progress so each hint is a clean −5. */
  const [hintedKeys, setHintedKeys] = useState(() => new Set());
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const completingRef = useRef(false);
  const fillRef = useRef(fill);
  const dirRef = useRef(dir);
  const activeWordIdRef = useRef(activeWordId);
  const isAutoNavRef = useRef(false);
  const completedRef = useRef(new Set());
  const firstHintUsedRef = useRef(false);
  const hintsUsedRef = useRef(0);

  /**
   * Live HUD: typed-correct letters / total × 100, minus hint penalties (−5 each).
   * Hint-revealed letters are omitted from progress so a hint drops the score by
   * exactly CROSSWORD_HINT_PENALTY (not −5 plus a +2/+3 progress bump).
   */
  const liveStagePoints = useMemo(() => {
    const { correct, total } = scoreCrosswordFill(fill, solution);
    let hintedCorrect = 0;
    hintedKeys.forEach((k) => {
      if (String(fill[k] || "").toUpperCase() === solution[k]) {
        hintedCorrect += 1;
      }
    });
    const typedCorrect = Math.max(0, correct - hintedCorrect);
    const base = liveProgressPoints(typedCorrect, total, CROSSWORD_POINTS);
    return base - hintsUsed * CROSSWORD_HINT_PENALTY;
  }, [fill, solution, hintsUsed, hintedKeys]);

  const displayPoints =
    status === "success"
      ? totalScore
      : Math.max(0, totalScore + liveStagePoints);

  useEffect(() => {
    fillRef.current = fill;
  }, [fill]);
  useEffect(() => {
    dirRef.current = dir;
  }, [dir]);
  useEffect(() => {
    activeWordIdRef.current = activeWordId;
  }, [activeWordId]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-1"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall1-stage4-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall1-stage4-page");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall1StageDone(stage, 4);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const highlightedKeys = useMemo(() => {
    const word = CROSSWORD_WORDS.find((w) => w.id === activeWordId);
    if (!word) return new Set();
    return new Set(getWordCellKeys(word));
  }, [activeWordId]);

  const lockedKeys = useMemo(() => {
    /** @type {Record<string, true>} */
    const map = {};
    Object.entries(cellStates).forEach(([k, state]) => {
      if (state === "correct") map[k] = true;
    });
    return map;
  }, [cellStates]);

  const focusWord = useCallback((word) => {
    setActiveWordId(word.id);
    activeWordIdRef.current = word.id;
    setDir(word.dir);
    dirRef.current = word.dir;
    const keys = getWordCellKeys(word);
    for (const k of keys) {
      if (lockedKeys[k]) continue;
      const { row, col } = parseCellKey(k);
      isAutoNavRef.current = true;
      if (focusCellInput(row, col)) return;
    }
    // all locked — still focus start
    const { row, col } = parseCellKey(keys[0]);
    isAutoNavRef.current = true;
    focusCellInput(row, col);
  }, [lockedKeys]);

  const focusSibling = useCallback(
    (row, col, step, dirOverride) => {
      const orientation = dirOverride || dirRef.current;
      let word = findWordAtCell(CROSSWORD_WORDS, row, col, orientation);
      if (!word && activeWordIdRef.current) {
        word = CROSSWORD_WORDS.find((w) => w.id === activeWordIdRef.current);
      }
      if (!word) return;
      const nextKey = siblingKeyInWord(word, row, col, step, lockedKeys);
      if (!nextKey) return;
      const next = parseCellKey(nextKey);
      isAutoNavRef.current = true;
      focusCellInput(next.row, next.col);
    },
    [lockedKeys]
  );

  const checkWordCompletion = useCallback((values) => {
    const newlyDone = [];
    CROSSWORD_WORDS.forEach((word) => {
      if (completedRef.current.has(word.id)) return;
      if (isWordComplete(word, values)) newlyDone.push(word.id);
    });
    if (!newlyDone.length) return;

    newlyDone.forEach((id) => completedRef.current.add(id));
    setCompletedWords((prev) => {
      const next = new Set(prev);
      newlyDone.forEach((id) => next.add(id));
      return next;
    });

    // Lock correct cells for completed words
    setCellStates((prev) => {
      const next = { ...prev };
      newlyDone.forEach((id) => {
        const word = CROSSWORD_WORDS.find((w) => w.id === id);
        if (!word) return;
        getWordCellKeys(word).forEach((k) => {
          next[k] = "correct";
        });
      });
      return next;
    });

    setFlashOk(true);
    window.setTimeout(() => setFlashOk(false), 1200);
  }, []);

  const handleCellInput = useCallback(
    (row, col, rawValue) => {
      if (status !== "playing") return;
      const key = cellKey(row, col);
      if (lockedKeys[key]) return;

      const val = String(rawValue || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "")
        .slice(-1);

      setFill((prev) => {
        const next = { ...prev, [key]: val };
        fillRef.current = next;
        checkWordCompletion(next);
        return next;
      });
      setErrorMsg("");

      setCellStates((prev) => {
        const next = { ...prev };
        if (!val) {
          if (next[key] !== "correct") delete next[key];
          return next;
        }
        next[key] = val === solution[key] ? "correct" : "wrong";
        return next;
      });

      const travelDir =
        dirRef.current || inferCellDirection(solution, row, col);
      if (!dirRef.current) {
        setDir(travelDir);
        dirRef.current = travelDir;
      }

      const activeWord = findWordAtCell(
        CROSSWORD_WORDS,
        row,
        col,
        travelDir
      );
      if (activeWord) {
        setActiveWordId(activeWord.id);
        activeWordIdRef.current = activeWord.id;
        setDir(activeWord.dir);
        dirRef.current = activeWord.dir;
      }

      if (val) {
        requestAnimationFrame(() => {
          focusSibling(row, col, 1, travelDir);
        });
      }
    },
    [status, lockedKeys, solution, checkWordCompletion, focusSibling]
  );

  const completePuzzle = useCallback(async () => {
    if (completingRef.current || status === "success" || status === "saving") {
      return;
    }
    completingRef.current = true;
    setStatus("saving");
    setErrorMsg("");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const score = Math.max(
      0,
      CROSSWORD_POINTS - hintsUsedRef.current * CROSSWORD_HINT_PENALTY
    );
    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const result = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 4,
          score,
          answers: {
            puzzle: "sports_archive_crossword",
            fill: fillRef.current,
            hintsUsed: hintsUsedRef.current,
          },
          time: timeStr,
        });
        if (result?.total_score != null) {
          setTotalScore(Number(result.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + score);
        }
      } else {
        setTotalScore((prev) => prev + score);
      }

      setEarnedPoints(score);
      markHall1HotspotCompleted("newspaper");
      unlockHall1Hotspot(CROSSWORD_UNLOCKS);
      setStatus("success");
    } catch (err) {
      console.error("Crossword save:", err);
      completingRef.current = false;
      setStatus("playing");
      setErrorMsg("Could not save progress. Please try again.");
    }
  }, [
    status,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
  ]);

  const handleCheck = useCallback(() => {
    if (status === "saving" || status === "success") return;
    const score = scoreCrosswordFill(fillRef.current, solution);
    if (!score.complete) {
      setErrorMsg("Fill every cell before checking the archive.");
      setShake(true);
      window.setTimeout(() => setShake(false), 420);
      return;
    }
    if (!isCrosswordSolved(fillRef.current, solution)) {
      setErrorMsg(
        `Not yet — ${score.correct}/${score.total} letters are correct. Keep going.`
      );
      setShake(true);
      window.setTimeout(() => setShake(false), 420);
      return;
    }
    completePuzzle();
  }, [status, solution, completePuzzle]);

  // Auto-finish when every word is complete (like reference)
  useEffect(() => {
    if (status !== "playing") return;
    if (completedWords.size === CROSSWORD_WORDS.length) {
      completePuzzle();
    }
  }, [completedWords, status, completePuzzle]);

  const applyHintReveal = useCallback(
    (word) => {
      if (!word || completedRef.current.has(word.id)) return false;
      const { fill: next, revealedKey } = revealLetterInWord(
        word,
        fillRef.current,
        solution
      );
      if (!revealedKey) return false;
      setFill(next);
      fillRef.current = next;
      setCellStates((prev) => ({ ...prev, [revealedKey]: "correct" }));
      setHintedKeys((prev) => {
        const nextKeys = new Set(prev);
        nextKeys.add(revealedKey);
        return nextKeys;
      });
      checkWordCompletion(next);
      const { row, col } = parseCellKey(revealedKey);
      focusCellInput(row, col);
      hintsUsedRef.current += 1;
      setHintsUsed(hintsUsedRef.current);
      setErrorMsg("");
      return true;
    },
    [solution, checkWordCompletion]
  );

  const handleHint = useCallback(() => {
    if (status !== "playing") return;

    if (!firstHintUsedRef.current) {
      const first = CROSSWORD_WORDS[0];
      firstHintUsedRef.current = true;
      setActiveWordId(first.id);
      setDir(first.dir);
      applyHintReveal(first);
      return;
    }

    const word =
      CROSSWORD_WORDS.find((w) => w.id === activeWordIdRef.current) ||
      CROSSWORD_WORDS[0];
    applyHintReveal(word);
  }, [status, applyHintReveal]);

  const handleReset = useCallback(() => {
    if (status !== "playing") return;
    setFill(createEmptyFill(solution));
    fillRef.current = createEmptyFill(solution);
    setCellStates({});
    setCompletedWords(new Set());
    completedRef.current = new Set();
    firstHintUsedRef.current = false;
    hintsUsedRef.current = 0;
    setHintsUsed(0);
    setHintedKeys(new Set());
    setErrorMsg("");
    focusWord(CROSSWORD_WORDS[0]);
  }, [status, solution, focusWord]);

  const cellMap = useMemo(() => {
    /** @type {Record<string, true>} */
    const map = {};
    cells.forEach((c) => {
      map[cellKey(c.row, c.col)] = true;
    });
    return map;
  }, [cells]);

  return (
    <div className="hall1-stage4 sports-archive">
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="sports-archive__inner">
        <header className="sports-archive__header">
          <h1 className="sports-archive__title">
            SPORTS ARCHIVE (CROSSWORD)
          </h1>
        </header>

        {flashOk ? (
          <p className="sports-archive__flash" role="status">
            Word locked in!
          </p>
        ) : null}

        <div className="sports-archive__layout">
          <aside className="sports-archive__sidebar">
            <div className="sports-archive__intro">
              <div className="sports-archive__intro-rule" aria-hidden />
              <h2 className="sports-archive__intro-title">SPORTS ARCHIVE</h2>
              <div className="sports-archive__intro-rule" aria-hidden />
              <p className="sports-archive__intro-text">
                Solve the crossword using the clues.
              </p>
            </div>

            <div
              className="sports-archive__clue-box"
              style={{ backgroundImage: `url(${cluesBox})` }}
            >
              <h2 className="sports-archive__clue-heading">CLUES</h2>
              <div className="sports-archive__clue-scroll">
                {[
                  { label: "ACROSS", words: acrossWords },
                  { label: "DOWN", words: downWords },
                ].map((group) =>
                  group.words.length ? (
                    <div key={group.label} className="sports-archive__clue-group">
                      <h3 className="sports-archive__clue-group-title">
                        {group.label}
                      </h3>
                      <ol className="sports-archive__clue-list">
                        {group.words.map((w) => {
                          const done = completedWords.has(w.id);
                          const active = activeWordId === w.id;
                          return (
                            <li key={w.id}>
                              <button
                                type="button"
                                className={`sports-archive__clue-btn${
                                  active
                                    ? " sports-archive__clue-btn--active"
                                    : ""
                                }${
                                  done ? " sports-archive__clue-btn--done" : ""
                                }`}
                                onClick={() => focusWord(w)}
                              >
                                <span className="sports-archive__clue-num">
                                  {w.number}.
                                </span>{" "}
                                {w.clue}
                                {done ? " ✓" : ""}
                              </button>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </aside>

          <div
            className={`sports-archive__board-wrap${
              shake ? " sports-archive__board-wrap--shake" : ""
            }`}
          >
            <div
              className="sports-archive__grid"
              style={{
                gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
                aspectRatio: `${cols} / ${rows}`,
              }}
              role="grid"
              aria-label="Sports archive crossword"
            >
              {Array.from({ length: rows * cols }).map((_, i) => {
                const r = Math.floor(i / cols);
                const c = i % cols;
                const k = cellKey(r, c);
                if (!cellMap[k]) {
                  return (
                    <div
                      key={k}
                      className="sports-archive__cell sports-archive__cell--block"
                      aria-hidden
                    />
                  );
                }
                const state = cellStates[k];
                const locked = state === "correct";
                const highlight = highlightedKeys.has(k);
                const preferDir = inferCellDirection(solution, r, c);

                return (
                  <div
                    key={k}
                    className={`sports-archive__cell${
                      highlight ? " sports-archive__cell--highlight" : ""
                    }${
                      state === "correct"
                        ? " sports-archive__cell--correct"
                        : ""
                    }${
                      state === "wrong" ? " sports-archive__cell--wrong" : ""
                    }`}
                  >
                    {numbers[k] != null ? (
                      <span className="sports-archive__cell-num">
                        {numbers[k]}
                      </span>
                    ) : null}
                    <input
                      className="sports-archive__input"
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      maxLength={1}
                      data-cw-row={r}
                      data-cw-col={c}
                      value={fill[k] || ""}
                      disabled={locked || status !== "playing"}
                      aria-label={`Row ${r + 1} column ${c + 1}`}
                      onFocus={() => {
                        if (isAutoNavRef.current) {
                          isAutoNavRef.current = false;
                          return;
                        }
                        const prefer = dirRef.current || preferDir;
                        const w = findWordAtCell(
                          CROSSWORD_WORDS,
                          r,
                          c,
                          prefer
                        );
                        if (w) {
                          setActiveWordId(w.id);
                          activeWordIdRef.current = w.id;
                          setDir(w.dir);
                          dirRef.current = w.dir;
                        }
                      }}
                      onChange={(e) => handleCellInput(r, c, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Tab") {
                          e.preventDefault();
                          const nextDir =
                            dirRef.current === "across" ? "down" : "across";
                          const w = findWordAtCell(
                            CROSSWORD_WORDS,
                            r,
                            c,
                            nextDir
                          );
                          if (w) focusWord(w);
                          else {
                            setDir(nextDir);
                            dirRef.current = nextDir;
                          }
                          return;
                        }
                        if (e.key.startsWith("Arrow")) {
                          e.preventDefault();
                          if (
                            e.key === "ArrowLeft" ||
                            e.key === "ArrowRight"
                          ) {
                            setDir("across");
                            dirRef.current = "across";
                          }
                          if (
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            setDir("down");
                            dirRef.current = "down";
                          }
                          const next = neighborCellKey(k, solution, e.key);
                          const { row, col } = parseCellKey(next);
                          focusCellInput(row, col);
                          return;
                        }
                        if (e.key === "Backspace" && !(fill[k] || "")) {
                          e.preventDefault();
                          focusSibling(
                            r,
                            c,
                            -1,
                            dirRef.current || preferDir
                          );
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {errorMsg ? (
          <p className="sports-archive__error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        <div className="sports-archive__actions">
          <div className="sports-archive__hint-wrap">
            <button
              type="button"
              className="sports-archive__action"
              onClick={handleHint}
              disabled={status !== "playing"}
            >
              <span className="sports-archive__action-icon" aria-hidden>
                💡
              </span>
              HINT
            </button>
            <span className="sports-archive__hint-cost">
              −{CROSSWORD_HINT_PENALTY} pts per hint
            </span>
          </div>
          <button
            type="button"
            className="sports-archive__action"
            onClick={handleReset}
            disabled={status !== "playing"}
          >
            <span className="sports-archive__action-icon" aria-hidden>
              ↻
            </span>
            RESET
          </button>
          <button
            type="button"
            className="sports-archive__submit"
            style={{ backgroundImage: `url(${greenBtn})` }}
            disabled={status === "saving"}
            onClick={handleCheck}
          >
            {status === "saving" ? "SAVING…" : "CHECK ARCHIVE"}
          </button>
        </div>
      </div>
      {status === "success" ? (
        <div className="sports-archive-success" role="dialog" aria-modal="true">
          <div className="sports-archive-success__veil" aria-hidden />
          <div className="sports-archive-success__card">
            <h2 className="sports-archive-success__title">ARCHIVE OPENED!</h2>
            <p className="sports-archive-success__text">
            Nice! The crossword points to today's newspaper.
            </p>
            <div className="sports-archive-success__stats">
              <span>{formatSecondsToClock(elapsedSec)}</span>
              <span>+{earnedPoints}</span>
            </div>
            <button
              type="button"
              className="sports-archive__submit"
              style={{ backgroundImage: `url(${greenBtn})` }}
              onClick={() => navigate("/hall-1")}
            >
              CONTINUE
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
